import RecordedChampion from '../../../components/RecordedChampion.jsx'
/* =========================================================================
 * HUNTED — everything the hunt has found, in one place.
 *
 * Brian: "There should also be a tab that has all the info that the gpt finds
 * in it."
 *
 * Until now a find was only visible in the moment: a bar that disappeared when
 * the selection cleared, or the one row somebody happened to be looking at. A
 * search that had already been paid for left nothing anybody could come back
 * to, and "have we already looked at this one?" had no answer on screen.
 *
 * IT READS THE RECORDS, NOT A SESSION LOG. Every hunt writes its provenance
 * onto the championship — `champion_searched_at`, `champion_search_attempts`,
 * `sr_champion_search`, `champion_source_url`, plus the story fields — and the
 * normalizer already carries all of it. So this tab is a lens over data that
 * outlives the tab, rather than a list that empties on refresh. Nothing new is
 * fetched and nothing is written.
 *
 * THE THREE STATES IT SEPARATES, because they want opposite responses:
 *   FOUND        a champion with a source. Ready to use.
 *   UNSOURCED    a champion with no source — a rumour, and the loudest row here.
 *   SEARCHED     looked for, nothing found. Retry later, or fix the record.
 * ========================================================================= */

import { useMemo, useState } from 'react'
import { formatDate } from '../../../lib/championships.js'
import { hostOf } from './huntJob.js'
import ChampionCell from './ChampionCell.jsx'
import styles from './pursuit.module.css'

/** Has anything ever hunted this record? Provenance, not a guess. */
function wasHunted(c) {
  return Boolean(
    c?.championSearchTag || c?.championSearchedAt ||
    Number(c?.championSearchAttempts || 0) > 0 || c?.championSourceUrl
  )
}

/* ⚠️ PURGATORY IS A TAB, because the team has to be able to STAND somewhere.
 *
 * Brian: "even if it's not a winner, it's worthy to tell my team, because if
 * not they will say the system doesn't work. Almost like a purgatory stage
 * until there is a confirmed winner."
 *
 * WAITING is the answer for most records most of the time, and it was the one
 * answer this board could not display: a record queued for the hunt, or one we
 * searched and could not settle, both fell into "Searched, nothing found" or
 * vanished from here entirely. So the honest state of the work — in motion,
 * not finished — had nowhere to live, and a rep checking the board saw only
 * finished things and concluded nothing was running.
 *
 * `waiting` sits FIRST, and it is deliberately the default tab. What is moving
 * matters more day to day than what is already settled, and putting Found
 * first made the board look empty on exactly the days it was busiest. */
const TABS = [
  { key: 'waiting', label: 'Waiting on a result' },
  { key: 'found', label: 'Found' },
  { key: 'unsourced', label: 'Unsourced' },
  { key: 'searched', label: 'Searched, nothing found' },
]

/* The tags that mean IN FLIGHT rather than finished. `not_found_human` is
 * deliberately NOT here — that one has been worked to a conclusion and keeps
 * its own tab, because "we tried and failed" is a different instruction to a
 * rep than "it is coming". */
const WAITING_TAGS = new Set(['requested', 'not_yet_occurred'])

export default function HuntedBoard({ items, onOpen, onAccepted }) {
  const [tab, setTab] = useState('waiting')

  const groups = useMemo(() => {
    const waiting = [], found = [], unsourced = [], searched = []
    for (const c of items || []) {
      if (!wasHunted(c)) continue
      const champ = (c.champion || c.winningSchool || '').trim()
      const tag = (c.championSearchTag || '').trim()
      /* Waiting is decided BEFORE the champion test, because a record can be
       * queued and still hold last season's name — and what a rep needs to
       * know about it is that a fresh answer is coming. */
      if (!champ && WAITING_TAGS.has(tag)) waiting.push(c)
      else if (champ && c.championSourceUrl) found.push(c)
      else if (champ) unsourced.push(c)
      else searched.push(c)
    }
    /* Most recently hunted first — the thing somebody is coming back to check
     * is almost always the thing that just ran. */
    const recent = (a, b) => String(b.championSearchedAt || '').localeCompare(String(a.championSearchedAt || ''))
    return { waiting: waiting.sort(recent), found: found.sort(recent), unsourced: unsourced.sort(recent), searched: searched.sort(recent) }
  }, [items])

  const total = groups.waiting.length + groups.found.length + groups.unsourced.length + groups.searched.length
  const list = groups[tab] || []

  if (!total) {
    return (
      <p className={styles.huntedEmpty}>
        Nothing has been hunted yet in the current filter. Press <b>Hunt champion</b> on any
        row — or run the sweep to find which records still need one — and the results collect here.
      </p>
    )
  }

  return (
    <div className={styles.hunted}>
      <div className={styles.huntedTabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.huntedTab} ${tab === t.key ? styles.huntedTabOn : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <span className={styles.huntedCount}>{groups[t.key].length}</span>
          </button>
        ))}
      </div>

      {/* The one that is not a category but a warning. A champion with no source
          is a rumour, and it should not sit quietly in a list of results. */}
      {tab === 'unsourced' && list.length > 0 && (
        <p className={styles.huntedWarn}>
          These have a champion recorded with <strong>no source</strong>. A champion
          without a link is not something to put in front of a customer — open each one
          and either find the source or clear it.
        </p>
      )}

      {tab === 'searched' && list.length > 0 && (
        <p className={styles.huntedNote}>
          These were searched and no champion could be established with a source. That is
          a real answer, not a failure — most often the result is not published yet.
          Attempts are shown so a record nobody can find is distinguishable from one
          nobody has tried since.
        </p>
      )}

      <ul className={styles.huntedList}>
        {list.map((c) => (
          <li key={c.id} className={styles.huntedRow}>
            <div className={styles.huntedMain}>
              <button type="button" className={styles.huntedName} onClick={() => onOpen && onOpen(c)}>
                {c.name}
              </button>
              <div className={styles.huntedMeta}>
                {[c.sport, c.level, c.state !== 'NATIONAL' ? c.state : null].filter(Boolean).join(' · ')}
                {c.championSearchedAt && ` · hunted ${formatDate(c.championSearchedAt)}`}
                {Number(c.championSearchAttempts || 0) > 1 && ` · ${c.championSearchAttempts} attempts`}
              </div>
              {/* The season line the hunt captured, where there is one. */}
              {c.summary?.text && <div className={styles.huntedSummary}>{c.summary.text}</div>}
            </div>

            <div className={styles.huntedRight}>
              {c.championSourceUrl && (
                <a className={styles.huntedSrc} href={c.championSourceUrl} target="_blank" rel="noreferrer">
                  {hostOf(c.championSourceUrl)} ↗
                </a>
              )}
              {/* A row with nothing found can be re-hunted from here directly —
                  which is the point of keeping the list at all. */}
              {c.champion && <RecordedChampion record={c} />}
              {!c.champion && <ChampionCell record={c} onDossier={onOpen} onAccepted={onAccepted} compact />}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
